import type { Service } from '@/services/firestore/types'
import { catalogPrice } from '@/utils/catalogDisplay'
import { MenuBulletList } from '@/components/printMenu/MenuBulletList'
import { MenuEyebrow } from '@/components/printMenu/MenuEyebrow'
import { MenuPageShell } from '@/components/printMenu/MenuPageShell'
import { MenuSectionBlock } from '@/components/printMenu/MenuSectionBlock'
import { MenuServiceEditorial } from '@/components/printMenu/MenuServiceEditorial'
import { MenuServiceImage } from '@/components/printMenu/MenuServiceImage'
import { MenuServiceMetaRow } from '@/components/printMenu/MenuServiceMetaRow'
import { cn } from '@/lib/utils'

type GelServicePageProps = {
  sectionEyebrow: string
  service: Service
  imageSrc: string
  imageAlt: string
  pageNumber: number
  totalPages: number
}

function GelServiceCopy({
  service,
  className,
}: {
  service: Service
  className?: string
}) {
  return (
    <div className={cn('flex flex-col', className)}>
      <header>
        <h2 className="font-display text-[length:var(--menu-fs-title)] font-medium leading-[1.15] tracking-[-0.02em] text-ink">
          {service.name}
        </h2>
        <MenuServiceMetaRow
          durationMin={service.durationMinutes}
          price={catalogPrice(service)}
          mostPopular={service.mostPopular}
          className="mt-4"
        />
        {service.subtitle ? (
          <p className="mt-3 font-display text-[length:var(--menu-fs-md)] italic text-muted">
            {service.subtitle}
          </p>
        ) : null}
      </header>

      <p className="mt-6 font-sans text-[length:var(--menu-fs-base)] leading-[1.65] text-body">
        {service.description}
      </p>

      <MenuServiceEditorial chooseWhen={service.chooseWhen ?? []} className="mt-5" />

      {service.footnote ? (
        <p className="mt-4 font-sans text-[length:var(--menu-fs-xs)] leading-relaxed italic text-muted">
          *{service.footnote}
        </p>
      ) : null}

      <div className="mt-5 border-t border-hairline/60 pt-5">
        <div className="grid grid-cols-2 gap-x-10">
          <MenuSectionBlock title="Included" divider={false}>
            <MenuBulletList items={service.included ?? []} />
          </MenuSectionBlock>

          <MenuSectionBlock title="Length" divider={false}>
            <ul className="space-y-2">
              {(service.lengthSurcharges ?? []).map((row) => (
                <li
                  key={row.label}
                  className="flex items-baseline justify-between gap-4 font-sans text-[length:var(--menu-fs-sm)] leading-snug"
                >
                  <span className={cn('text-body', row.emphasis && 'font-medium text-ink')}>
                    {row.label}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 tabular-nums',
                      row.emphasis ? 'font-medium text-ink' : 'text-muted',
                    )}
                  >
                    {row.value}
                  </span>
                </li>
              ))}
            </ul>
          </MenuSectionBlock>
        </div>
      </div>
    </div>
  )
}

export function GelServicePage({
  sectionEyebrow,
  service,
  imageSrc,
  imageAlt,
  pageNumber,
  totalPages,
}: GelServicePageProps) {
  const stacked = service.printImageLayout === 'stacked'

  return (
    <MenuPageShell pageNumber={pageNumber} totalPages={totalPages}>
      <MenuEyebrow>{sectionEyebrow}</MenuEyebrow>

      {stacked ? (
        <div className="mt-5 flex min-h-0 flex-1 flex-col overflow-hidden">
          <MenuServiceImage src={imageSrc} alt={imageAlt} layout="landscape" className="shrink-0" />
          <GelServiceCopy service={service} className="mt-5 min-h-0 flex-1 overflow-hidden" />
        </div>
      ) : (
        <div className="mt-8 grid min-h-0 flex-1 grid-cols-[1fr_1.15fr] gap-[10mm] overflow-hidden">
          <MenuServiceImage src={imageSrc} alt={imageAlt} />
          <GelServiceCopy service={service} className="pt-2" />
        </div>
      )}
    </MenuPageShell>
  )
}
